import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';

import { IFrameCommService } from '../iframe/i-frame-comm.service';
import { IFrameParentService } from '../iframe/i-frame-parent.service';
import { MessageType } from '../iframe/message-type.enum';
import { Message } from '../iframe/message.interface';
import { TopNavBarButton, TopNavOptions } from './top-bar-button.interface';

/**
 * Default settings for top navbar buttons
 */
export const defaultButton: TopNavBarButton = {
	id: '',
	placeInRow: 0,
	disabled: false,
	classes: 'btn-primary',
	icon: '',
	name: '',
	title: '',
	isSpinning: false,
	keepDisabledAfterClick: false,
	preventDefaultDisable: false,
	onClick: () => undefined,
	permission: '',
	disableTime: undefined,
};

@Injectable()
export abstract class TopBarService implements OnDestroy {
	public readonly closeDropdown$ = new Subject<string>();

	protected readonly destroy$ = new Subject<void>();

	/**
	 * Add a single button the the bar
	 * @param  button
	 */
	abstract addButton(button: TopNavBarButton): void;

	/**
	 * Add an array of buttons to the bar
	 * @param  buttons
	 */
	abstract addButtons(buttons: TopNavBarButton[]);

	/**
	 * Replace the current buttons with a new array of buttons
	 * @param  buttons
	 */
	abstract replaceButtons(buttons: TopNavBarButton[], clearInnerAppBtns?): void;

	/**
	 * Replace the current buttons with a new array of buttons
	 * @param  buttons
	 */
	abstract clearAllButtons(clearInnerAppBtns?): void;

	/**
	 * Removes a button from the topbar by id
	 * @param id ID of the button to remove
	 */
	abstract removeButtonById(id: string): void;

	/**
	 * Update a button
	 * @param update
	 */
	abstract updateButton(update: TopNavOptions): void;

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}

/**
 * Service to handle the top navigation buttons
 *
 * @export
 */
@Injectable({
	providedIn: 'root',
})
export class MainTopBarService extends TopBarService {
	/** Subject containing all topbar buttons */
	private buttons = new BehaviorSubject<TopNavBarButton[]>([]);

	// TODO: The delay can be removed if you set as-main component to changedetection on push
	// but all modals have to be updated... show() which sets the modal visibility to true is not detected by the CD!
	/** Observable for the header component */
	public buttons$ = this.buttons.pipe(delay(0));

	/**
	 * Constructor
	 */
	constructor(private comm: IFrameCommService, private iframeParent: IFrameParentService, private zone: NgZone) {
		super();
		// bind all methods to this class,
		this.addButton = this.addButton.bind(this);
		this.addButtons = this.addButtons.bind(this);
		this.clearAllButtons = this.clearAllButtons.bind(this);
		this.replaceButtons = this.replaceButtons.bind(this);
		this.listenToInnerApp = this.listenToInnerApp.bind(this);
		this.removeButtonById = this.removeButtonById.bind(this);
		this.updateButton = this.updateButton.bind(this);
		this.init();
	}

	closeDropdown(dropdownName: string) {
		this.closeDropdown$.next(dropdownName);
	}

	/**
	 * Initialization
	 */
	private init() {
		merge(this.comm.incomingMessages$, this.iframeParent.incomingMessages$)
			.pipe(
				filter((msg: Message) => msg.type === MessageType.topNav),
				takeUntil(this.destroy$)
			)
			.subscribe(m => this.zone.run(() => this.listenToInnerApp(m)));
	}

	/**
	 * Add a single button the the bar
	 * @param  button
	 */
	addButton(button: TopNavBarButton) {
		// Don't add duplicate buttons
		if (this.buttons.getValue().find(btn => button.id !== null && btn.id === button.id)) {
			return;
		}

		const sortIt = (x, y) => (x.placeInRow < y.placeInRow ? -1 : 1);
		const newButtons = [
			...this.buttons.getValue(),
			{
				...defaultButton,
				placeInRow: this.buttons.getValue().length,
				...button,
			},
		];

		newButtons.sort(sortIt);
		this.buttons.next(newButtons);
	}

	/**
	 * Add an array of buttons to the bar
	 * @param  buttons
	 */
	addButtons(buttons: TopNavBarButton[]) {
		buttons.forEach(this.addButton);
	}

	/**
	 * Updates button to refelct new state
	 * @param update takes an TopNavBarButton, and uses its ID to update
	 */
	updateButton(update: TopNavOptions) {
		const updatedButtons = this.buttons.getValue().map(btn => {
			if (btn.id === update.id) {
				return { ...btn, ...update };
			} else {
				return btn;
			}
		});

		this.buttons.next(updatedButtons);
	}

	/**
	 * Replace the current buttons with a new array of buttons
	 * @param  buttons
	 */
	replaceButtons(buttons: TopNavBarButton[], clearInnerAppBtns = true) {
		this.clearAllButtons(clearInnerAppBtns);
		this.addButtons(buttons);
	}

	/**
	 * Turn on/off spinning of an button
	 * @param  id the mandatory ID of the button you want to spin
	 * @param  isSpinning determines if the button should be spinning,
	 */
	spin(id: string, isSpinning: boolean) {
		// Check if the id is truthy, else the find will match on '' === ''
		if (!id) return;

		this.updateButton({
			id,
			isSpinning,
			disabled: isSpinning,
		});
	}

	/**
	 * Removes a button from the topbar by id
	 * @param id ID of the button to remove
	 */
	removeButtonById(id: string) {
		const newButtons = this.buttons.getValue().filter(btn => btn.id !== id);
		this.buttons.next(newButtons);
	}

	/**
	 * Remove all buttons for the top nav bar
	 */
	clearAllButtons(clearInnerAppBtns = true) {
		if (clearInnerAppBtns) {
			this.comm.send({
				type: MessageType.topNav,
				payload: { action: 'clearAllButtons' },
			});
		}
		this.buttons.next([]);
	}

	/**
	 * Listens to messages of the inner app
	 */
	private listenToInnerApp(msg: Message) {
		const { action, id, isSpinning } = msg.payload;
		switch (action) {
			case 'add': {
				const button: TopNavBarButton = msg.payload.button;
				button.onClick = () => {
					this.comm.send({
						type: MessageType.topNav,
						payload: { action: 'click', id: button.id },
					});
					this.iframeParent.send({
						type: MessageType.topNav,
						payload: { action: 'click', id: button.id },
					});
				};
				if (button.children) {
					for (const i of button.children.keys()) {
						button.children[i].onClick = () => {
							this.comm.send({
								type: MessageType.topNav,
								payload: { action: 'click', id: button.children[i].id },
							});
							this.iframeParent.send({
								type: MessageType.topNav,
								payload: { action: 'click', id: button.children[i].id },
							});
						};
					}
				}
				this.addButton(button);
				break;
			}
			case 'update': {
				const button: TopNavOptions = msg.payload.button;
				button.onClick = () => {
					this.comm.send({
						type: MessageType.topNav,
						payload: { action: 'click', id: button.id },
					});
					this.iframeParent.send({
						type: MessageType.topNav,
						payload: { action: 'click', id: button.id },
					});
				};
				if (button.children) {
					for (const i of button.children.keys()) {
						button.children[i].onClick = () => {
							this.comm.send({
								type: MessageType.topNav,
								payload: { action: 'click', id: button.children[i].id },
							});
							this.iframeParent.send({
								type: MessageType.topNav,
								payload: { action: 'click', id: button.children[i].id },
							});
						};
					}
				}
				this.updateButton(button);
				break;
			}
			case 'spin': {
				this.spin(id, isSpinning);
				break;
			}
			case 'remove': {
				this.removeButtonById(id);
				break;
			}
			case 'clearAllButtons': {
				this.clearAllButtons();
				break;
			}
		}
	}
}
